import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getSessionPersistSelector } from '../store/sessionPersist.selectors'
import {
  setIsHydrating,
  setShowModal,
  setPinValues,
  setIsResuming,
  setErrorMessage,
  setMetadata,
} from '../store/sessionPersist.actions'
import { sessionPersistService } from '../service/sessionPersist.service'
import { IPersistedMetadata } from '../service/sessionPersist.service'
import { verifyUserPin, handleOfflineLogin } from '../../../login/util/login.util'
import { handleApiError } from '../../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { hideLoader, showLoader } from '../../../../components/loader/store/loader.actions'
import { getRouterHistory } from '../../../../utilities/navigation.util'
import { AppRouterConstants } from '../../../../components/appRouter/AppRouter.constants'
import { SessionPinModal } from '../../components/SessionPinModal'
import { getDisplayUser } from '../../resume/utils/sessionResume.util'

const SessionPersistModal: React.FC = () => {
  const dispatch = useDispatch()
  const t = useTranslation()
  const history = getRouterHistory()
  const persistSession = useSelector(getSessionPersistSelector)
  const [shouldCloseAfterHydration, setShouldCloseAfterHydration] = useState(false)

  const setIsLoading = (loading: boolean) => {
    if (loading) {
      dispatch(showLoader(loading))
    } else {
      dispatch(hideLoader())
    }
  }

  const rehydrateUser = async (metadata: IPersistedMetadata): Promise<boolean> => {
    const { user: userState } = metadata
    dispatch(setIsHydrating(true))

    let succeeded = true

    const handleRehydrationFailure = (response: any): void => {
      succeeded = false
      setIsLoading(false)
      dispatch(setIsResuming(false))
      dispatch(setIsHydrating(false))
      dispatch(handleApiError(response))
      dispatch(setErrorMessage('Session restore failed. Please try again or start a new session.'))
    }

    const persistedAuthState = metadata.user?.authState
    const preferredUsername =
      persistedAuthState?.accessToken?.claims?.preferred_username?.toLowerCase() ||
      persistedAuthState?.idToken?.claims?.preferred_username?.toLowerCase()

    await handleOfflineLogin(
      preferredUsername || userState?.userName || '',
      dispatch,
      setIsLoading,
      handleRehydrationFailure,
      t,
      history,
      AppRouterConstants.HOME_ROUTE
    )
    return succeeded
  }

  useEffect(() => {
    if (!shouldCloseAfterHydration || persistSession.isHydrating) return
    dispatch(setIsResuming(false))
    dispatch(setShowModal(false))
    dispatch(setPinValues([]))
    dispatch(setErrorMessage(''))
    setShouldCloseAfterHydration(false)
  }, [dispatch, persistSession.isHydrating, shouldCloseAfterHydration])

  if (!persistSession.showModal) return null

  const handlePinChange = (numbers: string[]) => {
    setTimeout(() => {
      dispatch(setPinValues(numbers))
      dispatch(setErrorMessage(''))
    }, 0)
  }

  const handleResume = async () => {
    const pin = persistSession.pinValues.join('')

    if (!persistSession.metadata) return

    const result = await verifyUserPin(
      pin,
      persistSession.metadata.user?.userId.toString() || '',
      persistSession.metadata.user?.serviceId?.toString() || ''
    )

    if (result.success) {
      dispatch(setIsResuming(true))
      const rehydrateSucceeded = await rehydrateUser(persistSession.metadata)

      if (rehydrateSucceeded) {
        setShouldCloseAfterHydration(true)
      } else {
        setShouldCloseAfterHydration(false)
      }
    } else {
      dispatch(setErrorMessage(result.error || 'Incorrect PIN, please try again'))
    }
  }

  const handleLogout = () => {
    window.location.href = AppRouterConstants.LOGIN_ROUTE
  }

  const handleStartNewSession = async () => {
    await sessionPersistService.clearMetadata()
    dispatch(setMetadata(null))
    dispatch(setPinValues([]))
    dispatch(setErrorMessage(''))
    handleLogout()
  }

  return (
    <SessionPinModal
      isOpen={persistSession.showModal}
      displayUser={getDisplayUser(persistSession.metadata?.user)}
      pinValues={persistSession.pinValues}
      errorMessage={persistSession.errorMessage}
      isResuming={persistSession.isResuming}
      onPinChange={handlePinChange}
      onResume={handleResume}
      onStartNewSession={handleStartNewSession}
    />
  )
}

export default SessionPersistModal
